import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { QualityControlViewApiService } from '../../../../views/quality-control/api/view.service';
import { RoutingService } from '../../../../core/services/routing.service';
import { Department } from '../../../../api/elite/types/department';
import { InspectionApiDto } from '../../../../api/elite/dto/inspection.dto';
import { HosesDepartmentApiService } from '../../api/department.service';
import { HosesWorkOrder } from '../../models/work-order';
import { HosesSubWorkOrder } from '../../models/sub-work-order';
import { ApiNtWorkOrderService } from '../../../../api/netsuite/routes/work-order.service';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AlertService } from '../../../../core/services/alert.service';
import { SchedulerSubWorkOrder } from '../../../../common/scheduler/models/sub-work-order';
import { ApiRouteTransactionService } from '../../../../api/elite/routes/transaction.service';
import { OpenSalesByDepartment } from '../../../../api/elite/types/transaction';

@Injectable({
  providedIn: 'root',
})
export class HosesQualityControlViewApiService extends QualityControlViewApiService {
  protected readonly apiDepartmentRouter = inject(HosesDepartmentApiService);
  protected readonly routingService = inject(RoutingService);
  private readonly ntWorkOrderRouter = inject(ApiNtWorkOrderService);
  private readonly transactionApiService = inject(ApiRouteTransactionService);
  private readonly alertService = inject(AlertService);

  getDepartment(): Observable<Department> {
    return this.apiDepartmentRouter.dataV2();
  }

  data(): Observable<HosesWorkOrder[]> {
    return this.getDepartment().pipe(
      switchMap((dep) => {
        const query = new URLSearchParams({
          stage: 'Quality Control',
          quantityOut: 'true',
          includeInspection: 'true',
        });
        return this.subWorkOrderApiRoute
          .getByTypeAndStage(dep.id, query.toString())
          .pipe(
            map((res: SchedulerSubWorkOrder[]) => {
              return this.transformItemToWorkOrder(res);
            })
          );
      })
    );
  }

  getData(): Observable<HosesWorkOrder[]> {
    return forkJoin({
      workOrders: this.data(),
      departmentSales: this.getOpenSalesByDepartment(),
      openSales: this.getOpenSales(),
    }).pipe(
      map(({ workOrders, openSales, departmentSales }) => {
        return workOrders.map((wo) => {
          const sales = new Set<string>();
          const found = openSales.find(
            (item) => item.workOrder === wo.transactionNumber
          );
          if (found) {
            sales.add(found.salesOrder);
          }

          const deptSales = departmentSales
            .filter((op) => op.itemId === wo.assemblyItem.itemId)
            .map((op) => op.saleOrder);
          deptSales.forEach((op) => sales.add(op));

          wo.salesOrder = [...sales].join(', ');
          wo.salesOrders = [...sales];
          return wo;
        });
      })
    );
  }

  override createInspection(
    body: InspectionApiDto['create']['body']
  ): Observable<InspectionApiDto['create']['result']> {
    return this.apiInspectionRouter.createInspection({
      ...body,
      validateSkip:
        body.fromStage === 'Quality Control Visual' ? undefined : 'shift',
    });
  }

  protected override transformItemToWorkOrder(
    res: SchedulerSubWorkOrder[]
  ): HosesWorkOrder[] {
    return res.map((el) => HosesSubWorkOrder.transformToWorkOrder(el));
  }

  private getOpenSales(): Observable<
    { workOrder: string; salesOrder: string }[]
  > {
    return this.ntWorkOrderRouter.withSalesOrder().pipe(
      catchError(() => {
        this.alertService.error(
          'No se pudieron cargar los datos de las sales orders'
        );
        return of([]);
      })
    );
  }

  private getOpenSalesByDepartment(): Observable<OpenSalesByDepartment[]> {
    return this.transactionApiService
      .getSalesByDepartment(this.apiDepartmentRouter.getDepartmentName())
      .pipe(
        catchError((err) => {
          console.log(err);
          return of([]);
        })
      );
  }
}
